import { Immutable } from "immer";
import React, { useEffect, useState } from "react";
import { c, msgid, ngettext, t } from "ttag";

import { reverse } from "@reactivated";

import { constants } from "@reactivated/constants";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import { loadCategory } from "../../api/products";
import { IProduct } from "../../models/catalogue";
import { DiscountDisplayMode } from "../../models/discountDisplay";
import {
    IProductCategoryID,
    isoMonths,
    isoProductCategoryID,
    isoWebPageURL,
} from "../../models/nominals";
import { ATTRIBUTE_CLASSES, ShopTileType } from "../../models/shop-compare";
import {
    getOptionValueDisplayName,
    getProductPrice,
} from "../../utils/catalogue";
import { format } from "../../utils/format";
import { notEmpty } from "../../utils/functional";
import { useCurrentBreakpoint } from "../../utils/hooks";
import { roundReviewRating } from "../../utils/math";
import { usePageSetting, useShouldShowPricing } from "../../utils/settings";
import {
    TILE_PRICE_OPTIONS,
    resolveTileVariant,
} from "../../utils/shopCompare";
import { sortProductOptions } from "../../utils/sorting";
import { AttributeList } from "../AttributeList";
import { ConfiguratorProductPrice } from "../ConfiguratorProductPrice";
import { Flair } from "../Flair";
import { Image } from "../Image";
import { Link } from "../Link";
import { RatingGraphic } from "../RatingGraphic";
import { RichText } from "../RichText";

import styles from "./index.module.scss";

const AppSlug = constants["tsicommon.types.AppSlug"];

type IShopTileContentProps = Immutable<{
    value: ShopTileType;
    tilesetId: number;
    compactPricing: boolean;
    selectedSize: string;
}>;

export const ShopTile = (props: IShopTileContentProps) => {
    const appSlug = usePageSetting("app_slug") || "";
    const shopTile = props.value.shop_tile;
    const variant = resolveTileVariant(shopTile, props.selectedSize);
    const variantUrl = variant?.link ?? shopTile.link;
    const category = shopTile.category?.id;
    const currentBreakpoint = useCurrentBreakpoint();
    const shouldUseCompact =
        props.compactPricing && !currentBreakpoint.belowLaptop;
    const shouldShowPricing = useShouldShowPricing();
    const enableEcom = usePageSetting("enable_ecom");
    const [categoryNumReviews, setCategoryNumReviews] = useState(0);
    const [categoryRating, setCategoryRating] = useState(0);

    const [rootProducts, setRootProducts] = useState<ReadonlyArray<IProduct>>(
        [],
    );

    const loadProducts = async (
        productCategoryID: IProductCategoryID,
    ): Promise<ReadonlyArray<IProduct>> => {
        return await loadCategory(productCategoryID);
    };

    useEffect(() => {
        if (category) {
            loadProducts(isoProductCategoryID.wrap(category))
                .then((products) => {
                    setRootProducts(products);
                })
                .catch((err) => {
                    console.warn(
                        `ShopTile ${shopTile.title} Failed to load products for Category ${category}:`,
                        err,
                    );
                });
        }
    }, [category]);

    useEffect(() => {
        // Collate review number and rating from all products in the category
        const numReviews = rootProducts.reduce((total, product) => {
            return total + product.num_reviews;
        }, 0);
        const ratingWeight = rootProducts.reduce((total, product) => {
            return total + (product.rating ?? 0) * product.num_reviews;
        }, 0);
        const rating = numReviews > 0 ? ratingWeight / numReviews : 0;

        setCategoryNumReviews(numReviews);
        setCategoryRating(roundReviewRating(rating));
    }, [rootProducts]);

    // Collate option_feel values from all products in rootProducts (i.e. all products from this Shop Tile's category)
    // Also build a value -> display name map for locale-aware display
    const feelDisplayNames = new Map<string, string>();
    const categoryFeelOptions = rootProducts
        .map((product) => {
            // Check if product has variants
            if (product.children && product.children.length > 0) {
                return product.children
                    .map((child) => {
                        const feel = child.attributes.option_feel;
                        if (feel?.value && typeof feel.value === "string") {
                            feelDisplayNames.set(
                                feel.value,
                                getOptionValueDisplayName(feel),
                            );
                        }
                        return feel?.value;
                    })
                    .filter(notEmpty);
            } else {
                // Fallback to parent product's feel options
                const feel = product.attributes.option_feel;
                if (feel?.value && typeof feel.value === "string") {
                    feelDisplayNames.set(
                        feel.value,
                        getOptionValueDisplayName(feel),
                    );
                }
                return feel?.value;
            }
        })
        .filter(notEmpty)
        .flat();

    // Flatten options again to remove multi-dimensional array
    const flattenedFeelOptions = categoryFeelOptions.flat().filter(notEmpty);
    const allFeelOptions = [...new Set(flattenedFeelOptions)];

    const sortedFeelOptions = sortProductOptions("option_feel", allFeelOptions);
    const attributeList = sortedFeelOptions.map((feelOption) => ({
        title:
            shopTile.feel_option_display_names?.[feelOption] ??
            feelDisplayNames.get(feelOption) ??
            feelOption,
        class:
            (
                Object.keys(ATTRIBUTE_CLASSES) as Array<
                    keyof typeof ATTRIBUTE_CLASSES
                >
            ).find((key) => ATTRIBUTE_CLASSES[key] === feelOption) || "",
    }));

    const hasVariant = variant !== null;
    const effectivePrice = variant?.price ?? null;
    const totalPrice = effectivePrice
        ? getProductPrice(effectivePrice, TILE_PRICE_OPTIONS)
        : null;
    // Following variables are necessary for i18n of the financing string
    const formattedTotalPrice = totalPrice ? format.money(totalPrice) : null;
    const financingMonths = effectivePrice?.per_month_term_length
        ? isoMonths.unwrap(effectivePrice.per_month_term_length)
        : 0;
    const financingMonthsLength = financingMonths.toString();
    const financeTermsUrl = isoWebPageURL.wrap(
        "/finance-your-purchase/#terms_and_conditions",
    );
    const formattedPerMonth = effectivePrice?.per_month
        ? format.money(effectivePrice.per_month)
        : "";
    const financingLink = (children: React.ReactNode) => (
        <Link className={styles.financingPlanLink} href={financeTermsUrl}>
            {children}
        </Link>
    );

    return (
        <div className={styles.tileContainer}>
            {shopTile && shopTile.primary_flair && (
                <div
                    className={`${
                        shouldUseCompact
                            ? styles.primaryFlairCompact
                            : styles.primaryFlair
                    }`}
                >
                    <Flair flair={shopTile.primary_flair} />
                </div>
            )}
            {shopTile && (
                <div className={styles.headerContainer}>
                    {shopTile.product_image && (
                        <Link
                            href={variantUrl}
                            aria-label={`View ${shopTile.title}`}
                        >
                            <Image
                                alt={shopTile.title}
                                src={shopTile.product_image.url}
                            />
                        </Link>
                    )}
                    <div
                        className={`${
                            shouldUseCompact
                                ? styles.contentContainerCompact
                                : styles.contentContainer
                        }`}
                    >
                        {shopTile.secondary_flair && (
                            <div
                                className={`${
                                    shouldUseCompact
                                        ? styles.secondaryFlairCompact
                                        : styles.secondaryFlair
                                }`}
                            >
                                <Flair flair={shopTile.secondary_flair} />
                            </div>
                        )}
                        <Link
                            href={variantUrl}
                            aria-label={`View ${shopTile.title}`}
                        >
                            <RichText
                                html={shopTile?.title || ""}
                                className={`${
                                    shouldUseCompact
                                        ? styles.titleCompact
                                        : styles.title
                                }`}
                            />
                        </Link>
                        {appSlug !== AppSlug.TEMPURCA && (
                            <Link
                                href={isoWebPageURL.wrap(
                                    `${variantUrl}#reviews_body`,
                                )}
                                className={styles.starRating}
                            >
                                <RatingGraphic
                                    cardClass={styles.starRating}
                                    cardSize="large"
                                    numReviews={
                                        categoryNumReviews > 0
                                            ? categoryNumReviews
                                            : shopTile.num_reviews
                                    }
                                    rating={
                                        categoryNumReviews > 0
                                            ? roundReviewRating(categoryRating)
                                            : (shopTile.rating ?? null)
                                    }
                                    reviewNumberClass={styles.reviewLink}
                                />
                            </Link>
                        )}
                        <RichText
                            html={shopTile?.description || ""}
                            className={`${
                                shouldUseCompact
                                    ? styles.descriptionCompact
                                    : styles.description
                            }`}
                        />
                        <div className={styles.attributeList}>
                            <AttributeList list={attributeList} />
                        </div>
                    </div>
                </div>
            )}
            <div
                className={`${
                    shouldUseCompact
                        ? styles.priceContainer
                        : styles.priceContainerHorizontal
                }`}
            >
                {shouldShowPricing && (
                    <div>
                        {hasVariant && effectivePrice && totalPrice ? (
                            <>
                                <ConfiguratorProductPrice
                                    price={effectivePrice}
                                    strikeThroughMSRP={false}
                                    discountDisplayMode={
                                        DiscountDisplayMode.SHOW_ABSOLUTE
                                    }
                                    actualPriceStyle={"shop"}
                                />
                                {effectivePrice.per_month &&
                                    effectivePrice.per_month_term_length && (
                                        <div
                                            className={`${
                                                shouldUseCompact
                                                    ? styles.financingPlanContainer
                                                    : styles.financingPlanContainerHorizontal
                                            }`}
                                        >
                                            <span>
                                                {richT(
                                                    ngettext(
                                                        msgid`or <perMonthLink>${formattedPerMonth}/mo<sup>2</sup></perMonthLink> for <monthsLink>${financingMonthsLength} month</monthsLink><sup>1</sup>`,
                                                        `or <perMonthLink>${formattedPerMonth}/mo<sup>2</sup></perMonthLink> for <monthsLink>${financingMonthsLength} months</monthsLink><sup>1</sup>`,
                                                        financingMonths,
                                                    ),
                                                    {
                                                        perMonthLink: (
                                                            children: React.ReactNode,
                                                        ) =>
                                                            financingLink(
                                                                children,
                                                            ),
                                                        monthsLink: (
                                                            children: React.ReactNode,
                                                        ) =>
                                                            financingLink(
                                                                children,
                                                            ),
                                                    },
                                                )}
                                            </span>
                                            <br />
                                            <span>
                                                {c(
                                                    "shop-tile-financing-payments",
                                                )
                                                    .t`(total payments of ${formattedTotalPrice})`}
                                            </span>
                                        </div>
                                    )}
                            </>
                        ) : (
                            <div>{t`Unavailable in this size`}</div>
                        )}
                    </div>
                )}
                <div
                    className={`${
                        shouldUseCompact
                            ? styles.buttonsCompact
                            : styles.buttons
                    }`}
                >
                    {hasVariant ? (
                        <Link
                            href={variantUrl}
                            className="button al-comparisongrid-shopnow"
                        >
                            {enableEcom ? t`Shop Now` : t`Explore`}
                            <span className="ada-screenreader-only">
                                {interpolate(t`for %(productName)s`, {
                                    productName: shopTile.title || "",
                                })}
                            </span>
                        </Link>
                    ) : (
                        <button
                            className="button al-comparisongrid-shopnow"
                            aria-label={interpolate(
                                t`%(productName)s is unavailable in the selected size`,
                                {
                                    productName: shopTile.title || "",
                                },
                            )}
                            disabled
                        >
                            {t`Unavailable`}
                        </button>
                    )}
                    {shopTile.compare_tiles &&
                        shopTile.compare_tiles.length > 0 && (
                            <a
                                // @ts-expect-error: reverse() expects Django route params, not always typed for FE
                                href={`${reverse("shop-tile", { shop_tile_pk: shopTile.id })}?tileset=${props.tilesetId}${props.selectedSize ? `&size=${encodeURIComponent(props.selectedSize)}` : ""}`}
                                className={`button al-comparisongrid-compare ${styles.compareButton}`}
                            >
                                {t`Compare`}
                            </a>
                        )}
                </div>
            </div>
        </div>
    );
};
